<script setup>
import BnDatePicker from '@/components/BnDatePicker.vue';
import BnMedicationWindowFields from '@/components/questionnaires/widgets/BnMedicationWindowFields.vue';
import BnToolbarBtn from '@/components/BnToolbarBtn';
import DataProvider from '@/components/DataProvider';
import { buildSegment, carrierOf, doseQuantityOf, hasStepDuration, isHeld, segmentChain, segmentOn, todayIn } from '@/common/medicationSchedule';
import {
    blankWindow,
    accountAdministrationWindow,
    enrolledSiteOf,
    filledTimes,
    grownTimes,
    HOLD_REASON_QUERY,
    holdReasonConceptOf,
    holdReasonItems as itemsForHoldReasons,
    holdReasonValueOf,
    intervalLabel as labelForInterval,
    isWindowComplete,
    segmentStartDay,
    windowFromSegment,
    windowPerInterval,
    windowSegmentInput,
    windowTypeOf,
} from '@/common/medicationWindow';
import { formatDate } from '@/filters/dateTimeFilters';
import { useAuthStore } from '@/stores/auth';
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    medication: {
        type: Object,
        required: true,
    },
    segment: {
        type: Object,
        default: null,
    },
    // The segment a new one is seeded from — its times, days and window, never its dates.
    templateSegment: {
        type: Object,
        default: null,
    },
    // The order's sig. Handed in rather than read off the carrier, which does not carry it
    // until the medication is saved — and this dialog opens before that on a new medication.
    sig: {
        type: String,
        default: '',
    },
    timeZone: {
        type: String,
        default: undefined,
    },
    initialType: {
        type: String,
        default: null,
    },
    segmentSaved: {
        type: Boolean,
        default: false,
    },
    // "Take 2 capsules twice daily": the order says how often, so the schedule says when and
    // must land on that many administrations. Absent means the order did not specify.
    orderFrequency: {
        type: Number,
        default: null,
    },
    // The interval belongs to the order on an ordinary schedule, never to the segment —
    // asking twice is what lets a sig describe a regimen the pass does not run. A taper
    // step is the exception and keeps the interval it was written with.
    orderPeriod: {
        type: Number,
        default: 1,
    },
    orderPeriodUnit: {
        type: String,
        default: 'd',
    },
    // Where this patient's medications can be given. Empty offers no choice at all, which is
    // the answer for a patient enrolled nowhere.
    sites: {
        type: Array,
        default: () => [],
    },
    // The user may not change this medication at all. Distinct from a segment that has
    // started: that one is about the segment, this one is about the user.
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

const authStore = useAuthStore();
const vuetifyDisplay = useDisplay();

const windowState = ref(blankWindow());
const start = ref(null);
const endsType = ref('cancellation');
const endsDate = ref(null);
const endsCount = ref(null);
const holdReasonConcepts = ref([]);
const site = ref(null);

// Where the patient is, which is what a window is given at unless it says otherwise.
const patientSite = computed(() => enrolledSiteOf(props.sites));

// A self-administered window is not the clinic's to give, but the drug still comes out of its
// cupboard. The place is the same fact; what happens there is not, so it is not called the same.
const siteLabel = computed(() => (windowState.value.selfAdministered === true ? 'Dispense site' : 'Administration site'));

// A time the picker cannot read leaves the model on its previous value, so saving would
// write a time the form is not showing.
const hasUnreadableTime = ref(false);

const isOpen = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const today = computed(() => todayIn(props.timeZone));

const chain = computed(() => segmentChain(props.medication));

const segmentStart = computed(() => segmentStartDay(props.segment));

const type = computed(() => windowState.value.type);

const intervalLabel = computed(() => labelForInterval(windowState.value));

// Counted per interval, not per day: Mon/Wed/Fri at 08:00 is three a week, not one.
const perInterval = computed(() => windowPerInterval(windowState.value));

// The order states step 1 of a taper. Every later step varies from it by design — that is
// what a frequency taper is — so a taper step is not measured against it.
const editingTaperStep = computed(() => hasStepDuration(props.segment));

const frequencyMismatch = computed(() => {
    if (editingTaperStep.value || !props.orderFrequency || type.value !== 'scheduled') return false;
    return perInterval.value !== props.orderFrequency;
});

const prnExceedsOrder = computed(() => Boolean(props.orderFrequency) && type.value === 'prn' && Number(windowState.value.maxPerDay) > props.orderFrequency);

const heldNow = computed(() => {
    const segment = segmentOn(props.medication, today.value, props.timeZone);
    return isHeld(segment) ? segment : null;
});

// Adding a segment while a hold is in force IS the release — the new segment's start is what
// closes the hold, since a started segment can never be given an end.
const isReleasing = computed(() => !props.segment && type.value !== 'hold' && Boolean(heldNow.value));

const dialogTitle = computed(() => {
    if (isReleasing.value) return 'Release hold';
    if (type.value === 'prn') return 'PRN options';
    if (type.value === 'hold') return props.segment ? 'Edit hold' : 'Hold medication';
    return props.segment ? 'Edit schedule' : 'Set schedule';
});

// What a new link in an existing chain does to the one before it. The chain is additive, so
// the start date is the whole answer and the user is told it before saving.
const supersedeMessage = computed(() => {
    if (props.segment || !chain.value.length || !start.value) return '';
    const on = formatDate(start.value);
    if (isReleasing.value) return `Doses resume on ${on}. The hold ends that day, and nothing is backfilled for it.`;
    if (type.value === 'hold') return `Doses stop on ${on} and stay stopped until the hold is released. Everything before then is unchanged.`;
    return `This replaces the schedule in force on ${on}. Everything before then is unchanged.`;
});

const chainIndex = computed(() => (props.segment ? chain.value.indexOf(props.segment) : -1));

// D3: a segment's successor is what closes it, so only the last link in the chain states an
// end of its own. Letting a middle one state a later end is how a taper comes to overlap the
// step that follows it.
const hasSuccessor = computed(() => chainIndex.value >= 0 && chainIndex.value < chain.value.length - 1);

const endIsChained = computed(() => hasSuccessor.value && !isReadOnly.value);

// The neighbours a segment sits between. The chain is ordered by start, and rechainSegments
// re-derives every sequence and every end from that order on save — so a start moved past a
// neighbour is a reorder, not an edit. It swaps which stored Dosage holds which slot, dose
// and all, which is how a taper written 2 -> 1 -> 0.5 comes back 2 -> 0.5 -> 1 with nothing
// said. The chain records history by accumulating segments and cannot express a reorder, so
// the window is bounded rather than silently obeyed.
const predecessorStart = computed(() => (chainIndex.value > 0 ? segmentStartDay(chain.value[chainIndex.value - 1]) : null));

const successorStart = computed(() => (hasSuccessor.value ? segmentStartDay(chain.value[chainIndex.value + 1]) : null));

const startOutOfChain = computed(() => {
    if (isReadOnly.value || !start.value || chainIndex.value < 0) return false;
    if (predecessorStart.value && start.value <= predecessorStart.value) return true;
    return Boolean(successorStart.value && start.value >= successorStart.value);
});

const chainBoundsMessage = computed(() => {
    if (!startOutOfChain.value) return '';
    const window = [predecessorStart.value ? `after ${formatDate(predecessorStart.value)}` : null, successorStart.value ? `before ${formatDate(successorStart.value)}` : null]
        .filter(Boolean)
        .join(' and ');
    return `This segment starts ${window}. Moving it past a neighbour would swap the two and their doses with them — add a segment instead of moving this one.`;
});

// A started segment is immutable: administrations and adherence math are already keyed to
// it. Only a segment already written to the record counts — until then it is a draft, and a
// start date in the past is just a date the user typed.
const hasStarted = computed(() => props.segmentSaved && Boolean(segmentStart.value && segmentStart.value <= today.value));

const isReadOnly = computed(() => props.readOnly || hasStarted.value);

const canDelete = computed(() => Boolean(props.segment && !isReadOnly.value));

const medicationName = computed(() => props.medication?.medication?.CodeableConcept?.text || '');

const carrier = computed(() => carrierOf(props.medication));

const carrierDose = computed(() => doseQuantityOf(carrier.value) ?? null);

// A taper step carries its own dose. Reading the carrier's here showed the dose the taper
// descends FROM on every step of it.
const segmentDose = computed(() => doseQuantityOf(props.segment) ?? null);

const isTaperStepDose = computed(() => Boolean(segmentDose.value) && Number(segmentDose.value?.value) !== Number(carrierDose.value?.value));

// The sig already states the dose, so the form does not ask for it again. A taper step is the
// exception: it descends from the dose the sig quotes, so it names its own.
const sigLine = computed(() => {
    const sig = props.sig || carrier.value?.timing?.code?.text || carrier.value?.text || '';
    if (!isTaperStepDose.value) return sig;
    const dose = [segmentDose.value?.value, segmentDose.value?.unit].filter(Boolean).join(' ');
    return [sig, dose ? `this step: ${dose}` : ''].filter(Boolean).join(' · ');
});

const holdReasonItems = computed(() => itemsForHoldReasons(holdReasonConcepts.value));

const holdReasonConcept = computed(() => holdReasonConceptOf(holdReasonConcepts.value, windowState.value.holdReasonCode));

const endsOptions = computed(() => {
    if (type.value === 'hold') {
        return [
            { value: 'cancellation', title: 'Until released' },
            { value: 'date', title: 'On date' },
        ];
    }
    if (type.value === 'prn') {
        return [
            { value: 'cancellation', title: 'Upon cancellation' },
            { value: 'date', title: 'On date' },
        ];
    }
    return [
        { value: 'cancellation', title: 'Upon cancellation' },
        { value: 'date', title: 'On date' },
        { value: 'count', title: 'After # administrations' },
    ];
});

const ends = computed(() => {
    if (endsType.value === 'date' && endsDate.value) return { type: 'date', date: endsDate.value };
    if (endsType.value === 'count' && endsCount.value) return { type: 'count', count: Number(endsCount.value) };
    return undefined;
});

// Mirrors what buildSegment throws on, so the throw is unreachable through the form.
const startsInPast = computed(() => !isReadOnly.value && Boolean(start.value && start.value < today.value));

// An end before the start bounds the segment to nothing, so it can never place a dose and
// the chain reads as a schedule that silently stopped.
const endBeforeStart = computed(() => endsType.value === 'date' && Boolean(start.value && endsDate.value) && endsDate.value < start.value);

// A window cannot run past the order it belongs to. The resolver stops doses at the order's end
// regardless; refusing it here keeps the schedule from stating an end the order ignores. Until
// cancelled and a count are left alone - the order's end is what bounds those.
const orderStart = computed(() => String(props.medication?.effective?.start || '').slice(0, 10) || null);
const orderEnd = computed(() => String(props.medication?.effective?.end || '').slice(0, 10) || null);
const runsPastOrder = computed(() => {
    if (isReadOnly.value || !orderEnd.value) return false;
    if (start.value && start.value > orderEnd.value) return true;
    return !hasSuccessor.value && endsType.value === 'date' && Boolean(endsDate.value) && endsDate.value > orderEnd.value;
});

const canSave = computed(() => {
    if (isReadOnly.value || !start.value) return false;
    if (startsInPast.value) return false;
    if (startOutOfChain.value) return false;
    if (hasUnreadableTime.value) return false;
    if (endsType.value === 'date' && !endsDate.value) return false;
    if (endBeforeStart.value) return false;
    if (runsPastOrder.value) return false;
    if (endsType.value === 'count' && !(Number(endsCount.value) > 0)) return false;
    if (!isWindowComplete(windowState.value)) return false;
    if (type.value === 'prn') return !prnExceedsOrder.value;
    return !frequencyMismatch.value;
});

const reset = () => {
    const segment = props.segment;
    // A replacement segment is seeded from the one it replaces — retyping the times and days
    // to change one of them is what the chain would otherwise cost. Dates are never inherited:
    // the start is the change, and the end belongs to the segment that stated it.
    const source = segment || props.templateSegment;
    const ownRepeat = segment?.timing?.repeat;

    const seeded = source ? windowFromSegment(source) : blankWindow();
    seeded.type = props.initialType || (source ? windowTypeOf(source) : 'scheduled');
    // The order owns the interval on an ordinary schedule. A taper step states its own —
    // that is what lets a taper slow down — so editing one must not flatten it back.
    if (!hasStepDuration(source)) {
        seeded.period = Number(props.orderPeriod) > 0 ? Number(props.orderPeriod) : 1;
        seeded.periodUnit = props.orderPeriodUnit || 'd';
    }
    // Only a new segment takes the account's window, e-prescribed or not; an existing one shows
    // the window it was written with, blank included.
    if (!segment) seeded.administrationWindow = source?.timing?.administrationWindow ?? accountAdministrationWindow(authStore.account);
    seeded.maxPerDay = source?.maxPerDay ?? props.orderFrequency ?? null;
    // A hold states its own reason every time; it is never carried over from another segment.
    seeded.holdReasonCode = holdReasonValueOf(segment);
    seeded.holdNotes = segment?.hold?.notes || '';
    if (!filledTimes(seeded).length) seeded.times = grownTimes({ ...seeded, times: [] }, props.orderFrequency);

    windowState.value = seeded;
    // Inheritance is for a NEW segment only. An existing one shows exactly what it names, blank
    // included - a site the user cleared means "wherever the patient is", and refilling it from
    // anywhere else would stamp that back on the next save. A new one takes the segment it is
    // seeded from, else where the patient is.
    site.value = (segment ? segment.administeringLocation?.id : (props.templateSegment?.administeringLocation?.id ?? patientSite.value)) || null;
    hasUnreadableTime.value = false;
    // The first window takes the order's dates by default: it opens when the order takes effect
    // if that is later than today, and ends when the order does. Only the first - a later window
    // is seeded from the segment it follows.
    const isFirstWindow = !segment && !segmentChain(props.medication).length;
    const deferredStart = isFirstWindow && orderStart.value && orderStart.value > today.value ? orderStart.value : null;
    start.value = segmentStart.value || deferredStart || today.value;
    endsDate.value = ownRepeat?.boundsPeriod?.end?.slice(0, 10) || null;
    if (isFirstWindow && orderEnd.value && orderEnd.value >= start.value) endsDate.value = orderEnd.value;
    endsCount.value = ownRepeat?.count ?? null;
    if (endsDate.value) endsType.value = 'date';
    else if (endsCount.value) endsType.value = 'count';
    else endsType.value = 'cancellation';
    // Neither a hold nor a PRN can be counted out in administrations.
    if (seeded.type !== 'scheduled' && endsType.value === 'count') endsType.value = 'cancellation';
};

watch(
    () => props.modelValue,
    (open) => {
        if (open) reset();
    },
    { immediate: true },
);

// The enrollments may still be reading when the dialog opens, so the default lands on whichever
// resolves second. Never over an answer already there - inherited or typed - and never onto an
// existing segment, whose blank site is itself an answer.
watch(patientSite, (id) => {
    if (props.modelValue && !props.segment && id && !site.value) site.value = id;
});

// Choosing days changes how many times a day the schedule needs. Grow to suit, but never
// discard a time already entered — the mismatch warning is what speaks to a wrong count.
watch(
    () => props.orderFrequency,
    (frequency) => {
        windowState.value = { ...windowState.value, times: grownTimes(windowState.value, frequency) };
    },
);

const close = () => {
    emit('update:modelValue', false);
};

const save = () => {
    const practitioner = authStore.practitioner;
    const dosage = buildSegment({
        medication: props.medication,
        sequence: props.segment?.sequence ?? chain.value.length + 1,
        ...windowSegmentInput(windowState.value, { holdReason: holdReasonConcept.value }),
        start: start.value,
        // Honoured at sequence 2 or more, which is what keeps a taper step's own dose through
        // an edit of its times rather than resetting it to the medication's.
        dose: segmentDose.value ?? undefined,
        // Without this an edited step stops being a taper step at all: the chain would read
        // as an ordinary schedule and the append flow would have nothing to follow.
        stepDuration: props.segment?.stepDuration ?? undefined,
        // The floor a segment expects doses above is its ORIGINAL creation, not this write.
        // Re-stamping it on an edit un-expects the slots already passed today, and the
        // reconciler then deletes doses a nurse still has to give.
        addedOn: props.segment?.addedOn ?? undefined,
        ends: ends.value,
        addedBy: practitioner?.id ? { id: practitioner.id, resourceType: practitioner.resourceType || 'Practitioner' } : undefined,
        administeringLocation: site.value ? { id: site.value, resourceType: 'Location' } : undefined,
    });

    emit('saved', dosage);
    close();
};

// Confirmed rather than immediate: this drops an entered schedule window with no undo.
const confirmingDelete = ref(false);

const remove = () => {
    confirmingDelete.value = false;
    emit('deleted', props.segment);
    close();
};
</script>

<template>
    <v-dialog v-model="isOpen" scrollable persistent :fullscreen="vuetifyDisplay.xs.value" max-width="600px">
        <v-card data-cy="medicationSegmentDialog" class="d-flex flex-column">
            <v-toolbar density="compact" flat class="flex-grow-0">
                <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
                <BnToolbarBtn type="secondary" label="Cancel" icon="times" data-cy="cancelSegmentBtn" @click="close" />
                <BnToolbarBtn v-if="!isReadOnly" type="primary" right label="Save" icon="check" :disabled="!canSave" data-cy="saveSegmentBtn" @click="save" />
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text class="pa-4 flex-grow-1 overflow-auto">
                <div v-if="medicationName" class="mb-4" data-cy="segmentMedicationReference">
                    <div class="text-body-2 font-weight-bold" data-cy="segmentMedicationName">{{ medicationName }}</div>
                    <div v-if="sigLine" class="text-caption text-medium-emphasis" data-cy="segmentSigLine">{{ sigLine }}</div>
                </div>

                <v-alert v-if="hasStarted" type="info" variant="tonal" density="compact" class="mb-4" data-cy="segmentReadOnlyAlert">
                    This segment has already started, so it can no longer be changed or deleted. Add a new segment to change the schedule from a future date.
                </v-alert>

                <v-alert v-else-if="readOnly" type="info" variant="tonal" density="compact" class="mb-4" data-cy="segmentViewOnlyAlert">
                    You can view this schedule but not change it. Ask a prescriber or an administrator to make a change.
                </v-alert>

                <v-alert v-if="supersedeMessage" type="info" variant="tonal" density="compact" class="mb-4" data-cy="segmentSupersedeAlert">
                    {{ supersedeMessage }}
                </v-alert>

                <v-alert v-if="endIsChained" type="info" variant="tonal" density="compact" class="mb-4 text-caption" data-cy="segmentChainedEndAlert">
                    This step runs until the next one begins, so the chain sets its end. Move its start to move the boundary.
                </v-alert>

                <div v-if="type !== 'prn' || chain.length > 1" class="d-flex flex-wrap align-start">
                    <div class="flex-shrink-0 mr-2" style="width: 150px">
                        <BnDatePicker v-model="start" :disabled="isReadOnly" :strict-date="false" label="Start date" data-cy="segmentStartDate"></BnDatePicker>
                    </div>
                    <v-select
                        v-model="endsType"
                        :items="endsOptions"
                        :disabled="isReadOnly || hasSuccessor"
                        :label="type === 'hold' ? 'Hold until' : 'Ends'"
                        class="mr-2 flex-grow-0 flex-shrink-0"
                        style="width: 215px"
                        data-cy="segmentEndsSelect"
                    ></v-select>
                    <div v-if="endsType === 'date'" class="flex-shrink-0" style="width: 150px">
                        <BnDatePicker
                            v-model="endsDate"
                            :disabled="isReadOnly || hasSuccessor"
                            :strict-date="false"
                            :error-messages="endBeforeStart ? ['The end date cannot be before the start date.'] : []"
                            label="End date"
                            data-cy="segmentEndDate"
                        ></BnDatePicker>
                    </div>
                    <v-text-field
                        v-else-if="endsType === 'count'"
                        v-model="endsCount"
                        :disabled="isReadOnly || hasSuccessor"
                        type="number"
                        min="1"
                        label="Administrations"
                        class="flex-grow-0 flex-shrink-0"
                        style="width: 160px"
                        data-cy="segmentEndCountField"
                    ></v-text-field>
                </div>

                <v-alert v-if="startsInPast" type="warning" variant="tonal" density="compact" class="mt-1 mb-2 text-caption" data-cy="startsInPastMessage">A schedule starts today or later.</v-alert>
                <v-alert v-if="runsPastOrder" type="warning" variant="tonal" density="compact" class="mt-1 mb-2 text-caption" data-cy="runsPastOrderMessage">
                    The order ends {{ formatDate(orderEnd) }}. End this window on or before then, or change the order's end date first.
                </v-alert>
                <v-alert v-if="startOutOfChain" type="warning" variant="tonal" density="compact" class="mt-1 mb-2 text-caption" data-cy="startOutOfChainMessage">
                    {{ chainBoundsMessage }}
                </v-alert>

                <!-- Where the clinic gives this window's doses, which is not where the patient is
                     unless nothing else is said. On the schedule rather than the order: a patient
                     steps down to another program and the next segment names the new place,
                     without the order being rewritten. Doses already made keep their own site. -->
                <v-select
                    v-if="sites.length && type === 'scheduled'"
                    v-model="site"
                    :items="sites"
                    :disabled="isReadOnly"
                    item-title="label"
                    item-value="id"
                    clearable
                    :label="siteLabel"
                    hide-details
                    class="mb-4"
                    style="max-width: 320px"
                    data-cy="segmentAdministrationSite"
                ></v-select>

                <BnMedicationWindowFields
                    :model-value="windowState"
                    data-cy="segment"
                    :read-only="isReadOnly"
                    :show-recurrence="editingTaperStep"
                    :interval-editable="editingTaperStep"
                    :frequency-editable="editingTaperStep"
                    :order-frequency="orderFrequency"
                    :hold-reasons="holdReasonItems"
                    :preview-from="start"
                    :preview-end="endsType === 'date' ? endsDate : null"
                    @patch="(apply) => (windowState = { ...windowState, ...apply(windowState) })"
                    @update:invalid="(value) => (hasUnreadableTime = value)"
                >
                    <template #scheduled-messages>
                        <v-alert v-if="frequencyMismatch" type="error" variant="tonal" density="compact" class="mb-3 text-caption" data-cy="frequencyMismatchMessage">
                            The order is {{ orderFrequency }} per {{ intervalLabel.replace('every ', '') }}, and this schedule gives {{ perInterval }}. Match the order to save.
                        </v-alert>
                    </template>
                    <template #prn-messages>
                        <v-alert v-if="prnExceedsOrder" type="warning" variant="tonal" density="compact" class="text-caption" data-cy="prnExceedsOrderMessage">
                            The order allows {{ orderFrequency }} per interval, so the maximum per day cannot be higher.
                        </v-alert>
                    </template>
                </BnMedicationWindowFields>

                <DataProvider v-model="holdReasonConcepts" collection :query="HOLD_REASON_QUERY" />
            </v-card-text>
            <template v-if="canDelete">
                <v-divider></v-divider>
                <v-card-actions class="pa-3">
                    <v-btn variant="text" color="error" data-cy="deleteSegmentBtn" @click="confirmingDelete = true">Delete segment</v-btn>
                </v-card-actions>
            </template>
        </v-card>

        <v-dialog v-model="confirmingDelete" persistent max-width="450">
            <v-card data-cy="confirmDeleteSegmentDialog">
                <v-card-title class="text-body-1 font-weight-bold">Delete this segment?</v-card-title>
                <v-card-text class="text-body-2">The times and days entered here are removed from the schedule. This cannot be undone.</v-card-text>
                <v-card-actions class="pa-3">
                    <v-spacer />
                    <v-btn variant="tonal" color="secondary" data-cy="cancelDeleteSegmentBtn" @click="confirmingDelete = false">Cancel</v-btn>
                    <v-btn variant="flat" color="error" data-cy="confirmDeleteSegmentBtn" @click="remove">Delete segment</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>
